import { useState, useEffect, useRef, useCallback } from "react";
import TextareaAutosize from 'react-textarea-autosize';
import { convertDateToISOString } from "@/helpers";
import { DatePickerControl } from "./DatePickerControl";
import { TaskInputProps, ExtendedTask } from "./types";
import { useClickOutside } from "../../hooks/useClickOutside";

const TaskInput = ({ 
  task, 
  onClose,
  onSubmit 
}: TaskInputProps) => {
  // State management
  const [title, setTitle] = useState(task?.title || "");
  const [date, setDate] = useState<Date | null>(task?.due ? new Date(task.due) : null);
  const [details, setDetails] = useState<string>(task?.notes || "");
  // Track if datepicker is open to prevent closing the task input
  const [isDatePickerActive, setIsDatePickerActive] = useState(false);
  
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLTextAreaElement>(null);
  
  // Handle clicks outside of the component with special handling for datepicker
  useClickOutside(containerRef, () => {
    // Don't close if the datepicker is active
    if (isDatePickerActive) return;
    
    // If there's a title, save the task before closing
    if (title.trim()) {
      submitTask('clickOutside');
    } else {
      onClose();
    }
  });

  // Focus and select input text when component mounts
  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();
      const len = titleInputRef.current.value.length;
      titleInputRef.current.setSelectionRange(0, len);
    }
  }, []);

  // Task submission
  const submitTask = useCallback((submissionMethod?: 'enter' | 'clickOutside' | 'datePicker') => {
    if (!title.trim()) {
      return; // Don't submit empty tasks
    }
    
    const taskData: Partial<ExtendedTask> = {
      title: title.trim(),
      notes: details || undefined,
    };
    
    if (date) {
      taskData.due = convertDateToISOString(date);
    }
    
    // If this is an existing task, include its ID
    if (task?.id) {
      taskData.id = task.id;
    }
    
    onSubmit(taskData, submissionMethod);
  }, [title, details, date, task, onSubmit]);

  // Keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitTask('enter');
    }
    if (e.key === "Escape") {
      onClose();
    } 
  };

  return (
    <div 
      ref={containerRef} 
      className="flex flex-col bg-muted w-full px-4"
    >
      {/* Title input */}
      <TextareaAutosize
        ref={titleInputRef}
        autoFocus
        className="w-full px-2 focus-visible:ring-0 focus-visible:ring-offset-0 border-transparent bg-muted text-sm max-md:text-base py-2 outline-none resize-none"
        style={{ boxSizing: "border-box" }}
        placeholder="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      
      {/* Details input */}
      <TextareaAutosize 
        value={details} 
        onKeyDown={handleKeyDown} 
        onChange={(e) => setDetails(e.target.value)} 
        placeholder="Details" 
        className="focus-visible:ring-0 focus-visible:ring-offset-0 border-transparent py-0 text-xs max-md:text-base h-4 bg-muted outline-none resize-none px-2"
      />
      
      {/* Date picker with control over its open state */}
      <div onClick={(e) => e.stopPropagation()}>
        <DatePickerControl 
          date={date} 
          setDate={setDate} 
          onOk={(selectedDate) => {
            if (!title.trim()) return;
            const taskData: Partial<ExtendedTask> = {
              title: title.trim(),
              notes: details || undefined,
              due: selectedDate ? convertDateToISOString(selectedDate) : null,
            };
            if (task?.id) {
              taskData.id = task.id;
            }
            onSubmit(taskData, 'datePicker');
          }}
          onOpenChange={setIsDatePickerActive}
        />
      </div>
    </div>
  );
};

export default TaskInput; 